import * as React from "react";
import { Link } from "@tanstack/react-router";
import { Menu, Phone, ArrowRight } from "lucide-react";

import { cn } from "@/lib/utils";
import { NAP, SHOW_PHONE } from "@/lib/business";
import { aiServiceLinks, primaryNavLinks, resourceLinks, BOOK_CTA_LABEL } from "@/lib/nav";
import { Button } from "@/components/ui/button";
import { CtaButton } from "@/components/CtaButton";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
  navigationMenuTriggerStyle,
} from "@/components/ui/navigation-menu";
import { Sheet, SheetClose, SheetContent, SheetTitle, SheetTrigger } from "@/components/ui/sheet";

function useScrolled(threshold = 24) {
  const [scrolled, setScrolled] = React.useState(false);
  React.useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > threshold);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [threshold]);
  return scrolled;
}

export function Header() {
  const scrolled = useScrolled(24);
  const [open, setOpen] = React.useState(false);
  const [menuValue, setMenuValue] = React.useState("");
  const headerRef = React.useRef<HTMLElement | null>(null);

  // Radix consumes the first outside pointer interaction to dismiss the open
  // panel, which swallows clicks on other header links. Clear our own state on
  // the capture phase without preventing/stopping the event so the click lands.
  React.useEffect(() => {
    if (!menuValue) return;
    const el = headerRef.current;
    if (!el) return;
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as Element | null;
      if (!target) return;
      if (target.closest("[data-nav-panel]")) return;
      if (target.closest("button[aria-expanded]")) return;
      setMenuValue("");
    };
    document.addEventListener("pointerdown", onPointerDown, true);
    return () => document.removeEventListener("pointerdown", onPointerDown, true);
  }, [menuValue]);

  // Radix toggles the trigger on click. A *mouse* click on an already-open
  // trigger must not close it. Touch taps and keyboard activation still toggle.
  const pointerTypeRef = React.useRef<string>("");
  const triggerPointerDown = (event: React.PointerEvent) => {
    pointerTypeRef.current = event.pointerType;
  };
  const triggerClick = (value: string) => (event: React.MouseEvent) => {
    const isMouse = event.detail > 0 && pointerTypeRef.current !== "touch";
    if (isMouse && menuValue === value) {
      event.preventDefault();
    }
    pointerTypeRef.current = "";
  };

  return (
    <header
      ref={headerRef}
      className={cn(
        // Opaque on phones: a blurred sticky header repaints through every
        // scroll frame, which is expensive on mobile Safari.
        "sticky top-0 z-50 w-full border-b border-ink-200 bg-paper-50 transition-brand md:bg-paper-50/80 md:backdrop-blur-[12px]",
        scrolled && "shadow-[0_1px_12px_oklch(0.1831_0.0309_263.38_/_0.06)]",
      )}
    >
      <div
        className="mx-auto flex h-[72px] w-full items-center justify-between gap-3"
        style={{ maxWidth: "1200px", paddingInline: "clamp(20px, 5vw, 48px)" }}
      >
        <Link
          to="/"
          className="inline-flex min-h-[44px] shrink-0 items-center whitespace-nowrap font-display text-body-sm font-semibold text-ink-900 md:text-body"
        >
          <span>
            LAA <span className="text-brand-700">Concierge</span>{" "}
            <span className="font-normal">Consulting</span>
          </span>
        </Link>

        <NavigationMenu className="hidden lg:flex" value={menuValue} onValueChange={setMenuValue}>
          <NavigationMenuList>
            <NavigationMenuItem value="services">
              <NavigationMenuTrigger
                onPointerDown={triggerPointerDown}
                onClick={triggerClick("services")}
                className="bg-transparent px-3 text-body-sm font-medium text-ink-600 transition-brand hover:bg-transparent hover:text-ink-900 focus:bg-transparent data-[state=open]:bg-transparent data-[state=open]:text-ink-900"
              >
                Services
              </NavigationMenuTrigger>
              <NavigationMenuContent data-nav-panel>
                <div className="grid w-[600px] grid-cols-[1fr_1.1fr] gap-6 p-6">
                  {[{ heading: "AI Practice", links: aiServiceLinks }].map(
                    (column: {
                      heading: string;
                      links: readonly { label: string; to: string }[];
                    }) => (
                      <div key={column.heading}>
                        <h3 className="px-3 pb-2 font-mono text-eyebrow uppercase tracking-[0.16em] text-brand-700">
                          {column.heading}
                        </h3>
                        <ul className="space-y-1">
                          {column.links.map((link) => (
                            <li key={link.to}>
                              <NavigationMenuLink asChild>
                                <Link
                                  to={link.to as never}
                                  className="block rounded-md px-3 py-2 text-body-sm text-foreground transition-brand hover:bg-secondary"
                                >
                                  {link.label}
                                </Link>
                              </NavigationMenuLink>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ),
                  )}

                  <NavigationMenuLink asChild>
                    <Link
                      to="/tools/ai-opportunity-scorecard"
                      className="flex h-full flex-col justify-between rounded-lg border border-ink-200 bg-paper-0 p-5 transition-brand hover:border-brand-300"
                    >
                      <span className="font-display text-body font-semibold text-foreground">
                        AI Opportunity Scorecard
                      </span>
                      <span className="mt-2 text-body-sm text-muted-foreground">
                        Free, 4 minutes. See where AI is most likely to pay off.
                      </span>
                      <span className="mt-4 inline-flex items-center gap-1 text-body-sm font-medium text-brand-700">
                        Start the scorecard <ArrowRight className="size-4" />
                      </span>
                    </Link>
                  </NavigationMenuLink>
                </div>
              </NavigationMenuContent>
            </NavigationMenuItem>

            {primaryNavLinks.map((link) => (
              <NavigationMenuItem key={link.to}>
                <NavigationMenuLink
                  asChild
                  className={cn(
                    navigationMenuTriggerStyle(),
                    "bg-transparent px-3 text-body-sm font-medium text-ink-600 transition-brand hover:bg-transparent hover:text-ink-900 focus:bg-transparent data-[state=open]:bg-transparent data-[state=open]:text-ink-900",
                  )}
                >
                  <Link to={link.to} activeProps={{ "data-active": "true" }}>
                    {link.label}
                  </Link>
                </NavigationMenuLink>
              </NavigationMenuItem>
            ))}

            <NavigationMenuItem value="resources">
              <NavigationMenuTrigger
                onPointerDown={triggerPointerDown}
                onClick={triggerClick("resources")}
                className="bg-transparent px-3 text-body-sm font-medium text-ink-600 transition-brand hover:bg-transparent hover:text-ink-900 focus:bg-transparent data-[state=open]:bg-transparent data-[state=open]:text-ink-900"
              >
                Resources
              </NavigationMenuTrigger>
              <NavigationMenuContent data-nav-panel>
                <ul className="w-[300px] space-y-1 p-4">
                  {resourceLinks.map((link) => (
                    <li key={link.to}>
                      <NavigationMenuLink asChild>
                        <Link
                          to={link.to}
                          className="block rounded-md px-3 py-2 text-body-sm text-foreground transition-brand hover:bg-secondary"
                        >
                          {link.label}
                        </Link>
                      </NavigationMenuLink>
                    </li>
                  ))}
                </ul>
              </NavigationMenuContent>
            </NavigationMenuItem>
          </NavigationMenuList>
        </NavigationMenu>

        <div className="flex shrink-0 items-center gap-3">
          {SHOW_PHONE ? (
            <a
              href={NAP.phoneHref}
              className="hidden shrink-0 items-center gap-2 whitespace-nowrap font-mono text-body-sm text-ink-600 transition-brand hover:text-ink-900 xl:inline-flex"
            >
              <Phone className="size-4" aria-hidden="true" />
              <span className="whitespace-nowrap">{NAP.phone}</span>
            </a>
          ) : null}
          <CtaButton asChild size="sm" className="hidden sm:inline-flex">
            <Link to="/book">{BOOK_CTA_LABEL}</Link>
          </CtaButton>

          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="lg:hidden" aria-label="Open menu">
                <Menu className="size-5" />
              </Button>
            </SheetTrigger>
            <SheetContent
              side="right"
              className="flex w-full max-w-none flex-col gap-0 p-0 sm:max-w-none"
            >
              <SheetTitle className="sr-only">Site navigation</SheetTitle>
              <nav className="flex-1 overflow-y-auto px-6 pb-8 pt-20">
                <ul className="space-y-1">
                  {[
                    ...aiServiceLinks,
                    ...primaryNavLinks,

                    ...resourceLinks,
                    { label: "Contact", to: "/contact" },
                  ].map((link) => (
                    <li key={link.label}>
                      <SheetClose asChild>
                        <Link
                          to={link.to}
                          className="flex min-h-[48px] items-center py-2 font-display text-h3 text-foreground"
                        >
                          {link.label}
                        </Link>
                      </SheetClose>
                    </li>
                  ))}
                </ul>
              </nav>
              <div className="border-t border-border p-6 pb-[calc(1.5rem+env(safe-area-inset-bottom))]">
                <SheetClose asChild>
                  <CtaButton asChild className="w-full">
                    <Link to="/book">{BOOK_CTA_LABEL}</Link>
                  </CtaButton>
                </SheetClose>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}

export default Header;
